import React, {useState, useEffect, useRef, useContext} from 'react';
import {VariableSizeGrid as Grid} from 'react-window';
import ResizeObserver from 'rc-resize-observer';
import classNames from 'classnames';
import {Button, Popconfirm, Popover, Table} from 'antd';
import {
    FlagTwoTone, PlusOutlined
} from "@ant-design/icons";
import {EditableCell} from './editableTable/editableCell';
import {EditableRow} from './editableTable/editableRow';
import {checkPermission, isAdmin, renderRecord} from "../utils/util";
import {FHIR_TABLE_CELL_HEIGHT, USER_ROLE} from "../consts";
import FlaggedMappingContent from './editableTable/FlaggedMappingPopover';
import AddMappingFlag from './editableTable/AddMappingFlag';
import { DimaContext } from '../DimaContext';

export function VirtualTable(props) {
    const {columns, scroll} = props;
    const [tableWidth, setTableWidth] = useState(0);
    const [openPopover, setOpenPopover] = useState(false);
    const dimaContext = useContext(DimaContext);

    const mergedColumns = columns.map(column => {
        return {
            ...column,
            onCell: (record, rowIndex) => {
                return {
                    record: record,
                    editable: column.editable,
                    dataIndex: column.dataIndex,
                    title: column.title,
                    handleSave: props.handleSave,
                }
            }
        };
    });

    const gridRef = useRef();
    const [connectObject] = useState(() => {
        const obj = {};
        Object.defineProperty(obj, 'scrollLeft', {
            get: () => null,
            set: (scrollLeft) => {
                if (gridRef.current) {
                    gridRef.current.scrollTo({
                        scrollLeft,
                    });
                }
            },
        });
        return obj;
    });

    const resetVirtualGrid = () => {
        gridRef.current.resetAfterIndices({
            columnIndex: 0,
            shouldForceUpdate: true,
        });
    };

    function preventScroll(e){
        e.preventDefault();
        e.stopPropagation();
    
        return false;
    }
    
    /**
     * If the user is editing the value cell, prevent them from scrolling in the
     * table. If they are finished editing the cell, allow them to scroll again.
     */
    const moveToScrollMode = (editing) => {
        let doc = document.querySelector('#virtual-table')
        if (editing === true) {
            doc.addEventListener('wheel', preventScroll, {passive: false} )
        } else {
            doc.removeEventListener('wheel', preventScroll)
        }
    }

    useEffect(() => resetVirtualGrid, [tableWidth]);

    /**
     * Generate the appropriate popover based on whether a flag already exists for the mapping
     * @param row - Mapping for which a Popover is being generated
     * @param style - CSS styles
     * @returns {Node} - Table cell with a Popover component
     */
    const generateFlagPopover = (row, style) => {
        // Each Popover component has a unique callback function to handle its open/close
        function handlePopoverOpenChange (openValue) {
            if (openValue) {
                setOpenPopover(row.id);
            } else {
                setOpenPopover(false);
            }
        }

        if (row.flag_content) {
            return (
                <div style={{
                    width: style.width,
                    height: "50px",
                    fontSize: "25px",
                    display: "table",
                    textAlign: "center",
                    verticalAlign: "middle",
                }}>
                    <td>
                        <Popover
                            trigger="click"
                            onOpenChange={handlePopoverOpenChange}
                            open={row.id === openPopover}
                            content={
                                <FlaggedMappingContent
                                    mapping={row}
                                    getMapData={props.getMapData}
                                    openPopover={openPopover}
                                    handlePopoverOpenChange={handlePopoverOpenChange}
                                />
                            }
                            >
                            <FlagTwoTone />
                        </Popover>
                    </td>
                </div>
            )
        } else {
            return (
                <div style={{
                    width: style.width,
                    height: "50px",
                    fontSize: "10px",
                    display: "table",
                    textAlign: "center",
                    verticalAlign: "middle",
                }}>
                    <td>
                        <Popover
                            trigger="click"
                            onOpenChange={handlePopoverOpenChange}
                            open={row.id === openPopover}
                            content={
                                <AddMappingFlag
                                    mapping={row}
                                    getMapData={props.getMapData}
                                    openPopover={openPopover}
                                    handlePopoverOpenChange={handlePopoverOpenChange}
                                />
                            }
                        >
                            <PlusOutlined />
                        </Popover>
                    </td>
                </div>
            )
        }
    }

    const renderVirtualList = (rawData, {scrollbarSize, ref, onScroll}) => {
        const { allowTableEdits } = props;
        const canEditMapping = process.env.REACT_APP_FEATURE_PERMISSIONS_V2 ? (dimaContext && checkPermission(dimaContext.userData.permissions, 'editMapping')) : isAdmin(USER_ROLE);
        ref.current = connectObject;
        const totalHeight = rawData.length * 54;
        return (
            <Grid
                ref={gridRef}
                className="virtual-grid"
                columnCount={mergedColumns.length}
                columnWidth={(index) => {
                    const {width} = mergedColumns[index];
                    return totalHeight > scroll.y && index === mergedColumns.length - 1
                        ? width - scrollbarSize - 1
                        : width;
                }}
                height={scroll.y}
                rowCount={rawData.length}
                rowHeight={() => FHIR_TABLE_CELL_HEIGHT}
                width={tableWidth}
                onScroll={({scrollLeft}) => {
                    setOpenPopover(false);
                    onScroll({
                        scrollLeft,
                    });
                }}
            >
                {({columnIndex, rowIndex, style}) => {
                    const column1Width = tableWidth * 0.05;
                    const column2Width = tableWidth * 0.375;
                    const column3Width = tableWidth * 0.375;
                    const column4Width = tableWidth * 0.1;
                    const column5Width = tableWidth * 0.1;
                    if (columnIndex === 0) {
                        const style1 = Object.assign({}, style);
                        style1.width = column1Width;
                        const style2 = Object.assign({}, style);
                        style2.left = column1Width;
                        style2.width = column2Width;
                        const style3 = Object.assign({}, style);
                        style3.left = column1Width + column2Width;
                        style3.width = column3Width;
                        const style4 = Object.assign({}, style);
                        style4.left = column1Width + column2Width + column3Width;
                        style4.width = column4Width;
                        const style5 = Object.assign({}, style);
                        style5.left = column1Width + column2Width + column3Width + column4Width;
                        style5.width = column5Width;

                        return (
                            <EditableRow data-cy={`${rawData[rowIndex].id}`}>
                                <span
                                    className={classNames('virtual-table-cell', {
                                        'virtual-table-cell-last': columnIndex === mergedColumns.length - 1,
                                    })}
                                    style={style1}>
                                    {
                                        generateFlagPopover(rawData[rowIndex], style1)
                                    }
                                </span>
                                <span
                                    className={classNames('virtual-table-cell', {
                                        'virtual-table-cell-last': columnIndex === mergedColumns.length - 1,
                                    })}
                                    style={style2}>
                                    <EditableCell width={column2Width}
                                                  record={rawData[rowIndex]}
                                                  dataIndex="path"
                                                  children={rawData[rowIndex]["path"]}
                                                  editable={allowTableEdits && canEditMapping}
                                                  title="Path"
                                                  handleSave={props.handleSave}
                                                  moveToScrollMode={moveToScrollMode}/>
                                </span>
                                <span
                                    className={classNames('virtual-table-cell', {
                                        'virtual-table-cell-last': columnIndex === mergedColumns.length - 1,
                                    })}
                                    style={style3}
                                >
                                    <EditableCell width={column3Width}
                                                  record={rawData[rowIndex]}
                                                  dataIndex="value"
                                                  children={rawData[rowIndex]["value"]}
                                                  editable={allowTableEdits && canEditMapping}
                                                  title="Value"
                                                  handleSave={props.handleSave}
                                                  suggestions={props.suggestions}
                                                  moveToScrollMode={moveToScrollMode}
                                                  >
                                        {renderRecord(rawData[rowIndex], "value")}
                                    </EditableCell>
                                </span>
                                <span
                                    className={classNames('virtual-table-cell', {
                                        'virtual-table-cell-last': columnIndex === mergedColumns.length - 1,
                                    })}
                                    style={style4}
                                >
                                    <EditableCell width={column4Width}
                                                  record={rawData[rowIndex]}
                                                  dataIndex="driving"
                                                  children={rawData[rowIndex]["driving"]}
                                                  handleSave={props.handleSave}
                                                  editable={allowTableEdits && canEditMapping}
                                                  moveToScrollMode={moveToScrollMode}
                                                  />
                                </span>
                                <span
                                    className={classNames('virtual-table-cell', {
                                        'virtual-table-cell-last': columnIndex === mergedColumns.length - 1,
                                    })}
                                    style={style5}
                                >
                                    <EditableCell width={column5Width}
                                                  record={rawData[rowIndex]}
                                                  dataIndex="action"
                                                  children={rawData[rowIndex]["action"]}
                                                  handleSave={props.handleSave}
                                                  editable={allowTableEdits && canEditMapping}
                                                  moveToScrollMode={moveToScrollMode}>
                                        {
                                            (props.dataSource.length >= 1 && canEditMapping && allowTableEdits) ? (
                                                <Popconfirm title="Are you sure?"
                                                            onConfirm={() => props.handleDelete(rawData[rowIndex])}>
                                                    <Button style={{marginLeft: 20}}
                                                            disabled={!allowTableEdits}
                                                            data-cy={`${rawData[rowIndex].id}-delete-btn`}>
                                                        X
                                                    </Button>
                                                </Popconfirm>
                                            ) : (
                                                <Button style={{marginLeft: 20}}
                                                        disabled={true}>
                                                    X
                                                </Button>
                                            )
                                        }
                                    </EditableCell>
                                </span>
                            </EditableRow>
                        )
                    }
                    return null;
                }}
            </Grid>
        );
    };

    return (
        <ResizeObserver onResize={({width}) => {
            setTableWidth(width)
        }}>
            <Table
                id="virtual-table"
                className={`virtual-table${props.allowTableEdits ? "" : "-not-editable"}`}
                columns={mergedColumns}
                loading={props.loading}
                pagination={props.pagination}
                rowClassName={props.rowClassName}
                rowKey={props.rowKey}
                scroll={props.scroll}
                dataSource={props.dataSource}
                size={props.size}
                components={{
                    body: renderVirtualList
                }}
            />
        </ResizeObserver>
    );
}